export const measurementsArticle = {
    id: 'measurements',
    title: 'Измерения на карте',
    category: 'Режим Данные',
    requiredModule: null,
    sandboxType: 'measurements',
    summary: 'Инструменты геодезических замеров: Линейка (расстояние), Площадь (контур) и Координаты (WGS-84).',
    markdown: `
> **Группа инструментов «Измерения»** в режиме **«Данные»** предназначена для быстрых замеров дистанций, расчета площадей и определения точных географических координат без засорения базы данных лишними объектами.

---

## 1. Обзор инструментов группы «Измерения»

:::grid cols=3
:::card icon="ruler" title="1. Линейка"
Замер расстояний между объектами, проверка ширины дорог и отступов зданий от границ участков.
:::

:::card icon="polygon" title="2. Площадь"
Оперативная оценка размера свободной территории в квадратных метрах (м²) и сотках.
:::

:::card icon="crosshair" title="3. Координаты"
Определение точки WGS-84 в один клик, быстрое копирование и просмотр объектов под курсором.
:::
:::

---

## 2. Инструмент «Линейка» (замер расстояний)

Инструмент выполняет точные геодезические расчеты с учетом кривизны эллипсоида Земли, показывая длину каждого отдельного сегмента и общую итоговую протяженность пути.

:::card icon="click" title="Как измерить расстояние:"
1. В верхней панели переключитесь в режим **«Данные»** и выберите **«Линейка»** (повторный клик по кнопке сбрасывает и отключает инструмент).
2. **Кликайте по карте:** последовательно расставляйте контрольные поворотные точки маршрута с помощью оптического прицела (удерживайте **Shift** для строгого выравнивания углов).
3. **Фиксация замера:** нажмите клавишу **Enter** или кликните повторно по последней точке.
4. **Результат замера:** система сразу отобразит плашки с длиной каждого отдельного участка (м), а в конечной точке зафиксирует **общую итоговую длину маршрута**.
:::

> 💡 **Сценарии применения Линейки:**  
> • Проверка нормативных противопожарных отступов дома от забора соседа (не менее 3 м).  
> • Замер ширины проезжей части, проездов и пешеходных дорожек генплана.  
> • Оценка протяженности будущей ветки водопровода, линии электропередач или ограждения.

---

## 3. Инструмент «Площадь» (замер контура)

Позволяет очертить произвольный замкнутый контур и моментально узнать его площадь и длину каждой стороны без необходимости сохранять объект в паспорте генплана.

:::card icon="click" title="Как измерить площадь:"
1. В блоке инструментов измерений выберите **«Площадь»** (повторный клик деактивирует инструмент).
2. **Очертите границы:** кликами мыши последовательно расставьте вершины исследуемого пятна.
3. **Замыкание контура:** кликните по первой (стартовой) точке полигона или нажмите клавишу **Enter**.
4. **Результат замера:** в центре контура выводится точная площадь в **м²** и **сотках** вместе с общим периметром, а вдоль **каждой границы** отображается ее точная длина в метрах.
:::

> 💡 **Сценарии применения Площади:**  
> • Быстрая оценка пятна застройки перед созданием капитального строения.  
> • Замер площади оставшейся свободной земли на участке после посадки дома и гаража.  
> • Проверка площади лесопарковой зоны или общественной территории.

---

## 4. Инструмент «Координаты» (точка WGS-84 и объекты)

Служит для мгновенной геолокации конкретной точки генплана, удобного копирования координат и анализа наложенных слоев.

:::card icon="click" title="Как определить координаты точки:"
1. Выберите инструмент **«Координаты»** (иконка прицела).
2. **Кликните в любую точку на карте:** на карте появится маркер-перекрестие, а **в правом верхнем углу экрана** откроется карточка с информацией по точке.
3. **Карточка точки содержит:**
   * **Широту (Lat) и Долготу (Lng)** в международном формате WGS-84 с точностью до 6 знаков после запятой (например: \`56.838012, 60.597465\`).
   * **Кнопку копирования:** кликните по строке координат или иконке копирования, чтобы мгновенно скопировать их в буфер обмена для навигаторов или документов.
   * **Список объектов в точке:** система выводит названия всех участков, зданий, дорог или зон, перекрывающих место клика. Клик по названию объекта приближает камеру к нему.
:::

---

## 5. Горячие клавиши управления

| Клавиша | Назначение |
| :--- | :--- |
| **Shift** | Удерживайте для умной привязки линий под углами 90° и 45°. |
| **Enter** | Завершить и зафиксировать замер расстояния или площади. |
| **Esc** | Пошаговая отмена последней поставленной точки замера / закрытие карточки координат. |
| **Delete / Backspace** | Полная очистка активного замера. |
| **Клик по инструменту** | Нажатие на любой инструмент замера очищает холст и запускает новый замер. |

---

## 6. Интерактивный тренажёр измерений

Попробуйте все три инструмента измерений в действии прямо на интерактивном холсте:

{{sandbox:measurements}}
`
};
